import { Avatar } from '../components/bits';
import { useClients, useConfig, useOverview, useWorkspaces } from '../data/store';
import { euro, shortDate } from '../data/helpers';

interface Props {
  onOpenClient: (id: string) => void;
}

export function RevenueView({ onOpenClient }: Props) {
  const clients = useClients();
  const { revenue } = useOverview();
  const workspaces = useWorkspaces();
  const { plans } = useConfig();
  const today = new Date().toISOString().slice(0, 10);
  const mrr = clients.reduce((s, c) => s + c.mrr, 0);
  const max = revenue.length ? Math.max(...revenue.map((r) => r.mrr)) : 0;
  const delta = revenue.length >= 4 ? revenue[revenue.length - 1]!.mrr - revenue[revenue.length - 4]!.mrr : 0;

  const byPlan = plans.map((p) => {
    const list = clients.filter((c) => c.plan === p.id && c.mrr > 0);
    return { plan: p, count: list.length, total: list.reduce((s, c) => s + c.mrr, 0) };
  });

  const wsRows = workspaces
    .map((w) => {
      const cs = clients.filter((c) => c.workspaceId === w.id);
      return { w, count: cs.length, mrr: cs.reduce((s, c) => s + c.mrr, 0) };
    })
    .filter((r) => r.count > 0);

  const paying = clients.filter((c) => c.mrr > 0).sort((a, b) => b.mrr - a.mrr);
  const upcoming = clients
    .filter((c) => c.status !== 'paused' && c.nextInvoice >= today)
    .sort((a, b) => a.nextInvoice.localeCompare(b.nextInvoice))
    .slice(0, 5);

  return (
    <div className="view">
      <div className="view-pad rs-stack">
        <div className="rs-2col">
          <div className="rs-card">
            <div className="rs-card-head">
              <span className="rs-card-title">Ricavo mensile ricorrente</span>
              <span className="kpi-delta">+{euro(delta)} / 3 mesi</span>
            </div>
            <div className="rs-bigstat">
              <span className="v">{euro(mrr)}</span>
              <span className="u">/ mese · {euro(mrr * 12)} / anno</span>
            </div>
            <div className="rs-chart">
              {revenue.map((r) => (
                <div className="bar-col" key={r.label}>
                  <div className="bar" style={{ height: '100%' }}>
                    <div className="fill" style={{ height: `${(r.mrr / max) * 100}%` }} />
                  </div>
                  <span style={{ fontSize: 10.5, color: 'var(--mute)' }}>{r.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="rs-card">
            <div className="rs-card-head">
              <span className="rs-card-title">Per piano</span>
            </div>
            <div className="rs-breakdown">
              {byPlan.map((b) => (
                <div className="rs-brow" key={b.plan.id}>
                  <span style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>{b.plan.name}</span>
                    <span className="rs-mini-meta">{b.count} clienti · {euro(b.plan.price)}/mese</span>
                  </span>
                  <span className="kpi-val" style={{ fontSize: 20 }}>{euro(b.total)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="rs-2col">
          <div className="rs-card">
            <div className="rs-card-head">
              <span className="rs-card-title">MRR per spazio</span>
            </div>
            <div className="rs-dl">
              {wsRows.map((r) => (
                <div className="rs-dl-row" key={r.w.id}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <span className="rs-ws-dot" style={{ background: r.w.accent }} />
                    <span className="rs-dl-v" style={{ fontWeight: 600 }}>{r.w.name.replace(' (house)', '')}</span>
                    {r.w.house && <span className="rs-tag-house">house</span>}
                  </span>
                  <span className="rs-dl-v">{euro(r.mrr)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="rs-card">
            <div className="rs-card-head">
              <span className="rs-card-title">Prossime fatture</span>
            </div>
            <div className="rs-dl">
              {upcoming.map((c) => (
                <div className="rs-dl-row" key={c.id}>
                  <span className="rs-dl-k">{c.name}</span>
                  <span className="rs-dl-v">
                    {shortDate(c.nextInvoice)}
                    {c.mrr ? ` · ${euro(c.mrr)}` : ''}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="rs-card">
          <div className="rs-card-head">
            <span className="rs-card-title">Clienti per ricavo</span>
          </div>
          <div className="rs-dl">
            {paying.map((c) => (
              <button
                key={c.id}
                type="button"
                className="rs-dl-row"
                style={{ background: 'none', border: 0, borderBottom: '1px solid var(--rule-2, var(--rule))', width: '100%', cursor: 'pointer' }}
                onClick={() => onOpenClient(c.id)}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                  <Avatar client={c} size={28} />
                  <span className="rs-dl-v" style={{ fontWeight: 600 }}>{c.name}</span>
                </span>
                <span className="rs-dl-v">{euro(c.mrr)}/mese</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
